import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { getRegister } from '../data/register.ts';

/** Where the register on screen came from, said plainly — the app's one home for it. */
function backendLine(s: Readonly<State>): string {
  switch (s.backend) {
    case 'live':
      return s.backendNote || 'Register from D1';
    case 'unreachable':
      return `Bundled seed — ${s.backendNote}`;
    default:
      return 'Checking backend…';
  }
}

/**
 * The short form that fits a header pill: "D1 live · 5 records".
 *
 * The count is taken from the register in force rather than from `backendNote`,
 * which reads "5 records from D1" and would print the source twice.
 */
function shortLine(s: Readonly<State>): string {
  switch (s.backend) {
    case 'live':
      return `D1 live · ${getRegister().length} records`;
    case 'unreachable':
      return 'Bundled seed · offline';
    default:
      return 'Checking backend…';
  }
}

function dotColor(s: Readonly<State>): string {
  switch (s.backend) {
    case 'live':
      return 'var(--good)';
    case 'unreachable':
      return 'var(--warn)';
    default:
      return 'var(--txt3)';
  }
}

/**
 * Backend reachability, as a pill in the application header.
 *
 * This used to be a status bar docked across the foot of the window. It is the
 * same one fact, moved to where the eye already goes for "what is this app
 * doing" — and the long form survives as the pill's tooltip, so the reason a
 * deployment fell back to the bundled seed is still one hover away.
 */
/**
 * Writes recorded on this device that have not reached the register.
 *
 * Absent when there are none, which is almost always. When there are, it is the
 * most important thing on the screen: an inspector needs to know that what they
 * recorded at the structure is still only on their phone, and they need to be
 * able to push it without hunting for a menu.
 *
 * Same honesty the title bar already applies to which register is on screen —
 * the app says what it is showing and where it came from, and a write that has
 * not landed is exactly that kind of fact.
 */
export function queuePill(s: Readonly<State>): HTMLElement | null {
  if (!s.queued.length && !s.syncNote) return null;

  if (!s.queued.length) {
    return h('div', { class: 'statuspill', title: s.syncNote ?? '' },
      h('span', { class: 'statuspill__text', text: s.syncNote ?? '' }),
    );
  }

  const n = s.queued.length;
  return h(
    'button',
    {
      type: 'button',
      class: 'statuspill statuspill--warn',
      title:
        `${n} write${n === 1 ? '' : 's'} recorded on this device and not yet in the register. ` +
        'Click to send them now.',
      disabled: s.syncing || undefined,
      onclick: () => void actions.syncQueue(),
    },
    h('span', { class: 'statuspill__dot', style: { background: 'var(--warn, #c08a2e)' } }),
    h('span', {
      class: 'statuspill__text',
      text: s.syncing ? `Syncing ${n}…` : `${n} waiting to sync`,
    }),
  );
}

export function backendPill(s: Readonly<State>): HTMLDivElement {
  return h(
    'div',
    { class: 'statuspill', title: backendLine(s) },
    h('span', { class: 'statuspill__dot', style: { background: dotColor(s) } }),
    h('span', { class: 'statuspill__text', text: shortLine(s) }),
  );
}
